
import { useState } from "react";
import styles from "./columnConfig.module.scss";

interface ColumnConfigProps {
  /** Full choosable column set in display order — may include report-defined columns
   *  the server pruned from the current fetch (Spec C), not just the fetched header. */
  columns: string[];
  visible: string[];
  /** The report's default visible set (columns minus hidden) — target of "Default". */
  defaultVisible: string[];
  onChange: (visible: string[]) => void;
  displayNames?: Record<string, string>;
  /** Column tooltip texts keyed by lower-cased column name (sColumnToolTip). */
  tooltips?: Record<string, string>;
  /** TEXTCOLLECTION composite → sibling columns rendered inside its widget. Unchecked
   *  siblings are listed indented under the composite; a checked sibling is a standalone
   *  visible column and is listed as a normal top-level entry instead. */
  groups?: Record<string, string[]>;
}

const normalize = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();

export function ColumnConfig({ columns, visible, defaultVisible, onChange, displayNames, tooltips, groups }: ColumnConfigProps) {
  const [query, setQuery] = useState("");

  const toggle = (col: string) => {
    if (visible.includes(col)) {
      onChange(visible.filter((c) => c !== col));
    } else {
      // Re-insert at original position
      const updated = [...visible, col];
      updated.sort((a, b) => columns.indexOf(a) - columns.indexOf(b));
      onChange(updated);
    }
  };

  const titleOf = (col: string) => (displayNames?.[col.toLowerCase()] ?? col).replace(/\\n/g, " ").trim();

  // Titles that appear more than once get the discrete column name appended.
  const titleCounts = new Map<string, number>();
  for (const c of columns) {
    const t = normalize(titleOf(c));
    titleCounts.set(t, (titleCounts.get(t) ?? 0) + 1);
  }

  const q = normalize(query);
  const shown = q
    ? columns.filter((c) => normalize(titleOf(c)).includes(q) || c.toLowerCase().includes(q))
    : columns;

  // Group unchecked TEXTCOLLECTION siblings under their composite (checked siblings are
  // standalone columns and stay top-level). A sibling only nests when its composite is
  // itself shown (e.g. not filtered away).
  const shownSet = new Set(shown.map((c) => c.toLowerCase()));
  const checkedSet = new Set(visible.map((c) => c.toLowerCase()));
  const compositeOf = new Map<string, string>(); // sibling (lower) -> composite column
  if (groups) {
    for (const [comp, subs] of Object.entries(groups)) {
      for (const s of subs) {
        if (!compositeOf.has(s.toLowerCase())) compositeOf.set(s.toLowerCase(), comp);
      }
    }
  }
  const nestParent = (col: string): string | null => {
    if (checkedSet.has(col.toLowerCase())) return null;
    const comp = compositeOf.get(col.toLowerCase());
    return comp && shownSet.has(comp.toLowerCase()) ? comp : null;
  };
  const childrenOf = new Map<string, string[]>();
  for (const c of shown) {
    const comp = nestParent(c);
    if (comp) {
      const arr = childrenOf.get(comp.toLowerCase()) ?? [];
      arr.push(c);
      childrenOf.set(comp.toLowerCase(), arr);
    }
  }

  const row = (col: string, nested: boolean) => {
    const title = titleOf(col);
    const showColName = !title || (titleCounts.get(normalize(title)) ?? 0) > 1;
    const rawTip = tooltips?.[col.toLowerCase()];
    // Don't repeat the tooltip when it says the same thing as the header
    // (e.g. header "Product\nName" vs tooltip "Product Name").
    const tip = rawTip && normalize(rawTip) !== normalize(title) ? rawTip : undefined;
    return (
      <label key={col} className={nested ? `${styles.label} ${styles.nested}` : styles.label}>
        <input
          type="checkbox"
          checked={visible.includes(col)}
          onChange={() => toggle(col)}
        />
        <span className={styles.labelText}>
          {title || col}
          {showColName && title ? <span className={styles.colName}> ({col})</span> : null}
          {tip ? <span className={styles.tooltip}>{tip}</span> : null}
        </span>
      </label>
    );
  };

  return (
    <div>
      <div className={styles.actions}>
        <button type="button" className={styles.actionBtn} onClick={() => onChange([...defaultVisible])}>
          Default
        </button>
        <button type="button" className={styles.actionBtn} onClick={() => onChange([...columns])}>
          Show all
        </button>
        <button type="button" className={styles.actionBtn} onClick={() => onChange([])}>
          Clear all
        </button>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Filter columns…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {shown.map((col) => {
        if (nestParent(col)) return null; // rendered under its composite below
        const children = childrenOf.get(col.toLowerCase());
        if (!children) return row(col, false);
        return (
          <div key={col}>
            {row(col, false)}
            {children.map((sub) => row(sub, true))}
          </div>
        );
      })}
      {shown.length === 0 && <div className={styles.noMatch}>No columns match "{query}"</div>}
    </div>
  );
}
